// Columna izquierda del explorer (`.col-filters` del mock), en su orden: Búsqueda · Elegibilidad
// por nivel · Marca · Rango de precio · Financiera aceptada.
import { ChevronDown } from 'lucide-react';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { Input } from '@/kit';
import { cn } from '@/lib/cn';
import type { PlanLevel } from '@/domains/sales/wizard/types';
import { financierChips } from '../../pos/lib/financierChips';
import { PLAN_LEVELS } from '../../phones/lib/levelPlans';
import { countActiveFilters } from '../lib/catalogExplorer';
import { EMPTY_CATALOG_FILTERS, levelLabel, type CatalogFilters } from '../lib/catalogLevels';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const GROUP = 'mt-2.5';
const FIELD = 'h-[26px] text-[11.5px]';
const SELECT =
    'h-[26px] w-full appearance-none rounded-md border border-input bg-transparent pl-2 pr-[22px] text-[11.5px] outline-none focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50';
/** Colores de nivel del mock: apagado con borde y texto del nivel, prendido relleno. */
const LEVEL_CHIP_CLASS: Record<PlanLevel, { on: string; off: string }> = {
    GOLD: {
        on: 'border-tier-gold bg-tier-gold text-white',
        off: 'border-tier-gold text-tier-gold',
    },
    SILVER: {
        on: 'border-tier-silver bg-tier-silver text-white',
        off: 'border-tier-silver text-tier-silver',
    },
    BRONZE: {
        on: 'border-tier-bronze bg-tier-bronze text-white',
        off: 'border-tier-bronze text-tier-bronze',
    },
};
const CHIP =
    'w-full whitespace-nowrap rounded-full border px-1.5 py-0.5 text-center text-[9.5px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50';

export function CatalogFiltersPanel({
    filters,
    brands,
    financiers,
    levelsNote,
    levelsUnavailable,
    onChange,
    collapsed,
    onToggleCollapsed,
    searchRef,
    loadedNote = null,
}: {
    filters: CatalogFilters;
    /** Marcas que hay en el catálogo; el filtro ofrece lo que existe. */
    brands: string[];
    /** Financieras configuradas en el catálogo de esta tienda. */
    financiers: string[];
    /** «Máx: Oro $10K · …», o por qué no hay planes por nivel. */
    levelsNote: string | null;
    /** Sin planes legibles los chips de nivel se apagan. */
    levelsUnavailable: boolean;
    onChange: (next: CatalogFilters) => void;
    collapsed: boolean;
    onToggleCollapsed: () => void;
    searchRef?: React.Ref<HTMLInputElement>;
    /** Que el filtro mira lo cargado y no el catálogo entero. `null` cuando ya no quedan páginas. */
    loadedNote?: string | null;
}) {
    const patch = (partial: Partial<CatalogFilters>) => {
        onChange({ ...filters, ...partial });
    };
    const activeCount = countActiveFilters(filters) + (filters.level === '' ? 0 : 1);

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={activeCount}
            onReset={() => {
                onChange(EMPTY_CATALOG_FILTERS);
            }}
        >
            {loadedNote === null ? null : (
                <div
                    role="status"
                    className="mt-2 rounded border border-border bg-muted/40 px-2 py-1.5 text-[10px] leading-snug text-muted-foreground"
                >
                    {loadedNote}
                </div>
            )}

            <div className={GROUP}>
                <div className={LABEL}>Búsqueda</div>
                <Input
                    ref={searchRef}
                    type="search"
                    value={filters.query}
                    onChange={(e) => {
                        patch({ query: e.target.value });
                    }}
                    placeholder="SKU, marca o modelo"
                    aria-label="Buscar por SKU, marca o modelo"
                    className={FIELD}
                />
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Elegibilidad por nivel</div>
                <div className="grid grid-cols-4 gap-1" role="group" aria-label="Nivel">
                    {PLAN_LEVELS.map((level) => (
                        <LevelChip
                            key={level}
                            level={level}
                            active={filters.level === level}
                            disabled={levelsUnavailable}
                            title={levelsUnavailable ? (levelsNote ?? undefined) : undefined}
                            onClick={() => {
                                patch({ level: filters.level === level ? '' : level });
                            }}
                        />
                    ))}
                    <button
                        type="button"
                        aria-pressed={filters.level === ''}
                        onClick={() => {
                            patch({ level: '' });
                        }}
                        className={cn(
                            CHIP,
                            filters.level === ''
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border bg-card hover:bg-accent',
                        )}
                    >
                        Todos
                    </button>
                </div>
                {levelsNote === null ? null : (
                    <div className="mt-1 text-[10px] text-muted-foreground">{levelsNote}</div>
                )}
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Marca</div>
                <SelectField
                    value={filters.brand}
                    disabled={brands.length === 0}
                    label="Filtrar por marca"
                    onChange={(brand) => {
                        patch({ brand });
                    }}
                    options={[
                        { value: '', label: 'Todas' },
                        ...brands.map((brand) => ({ value: brand, label: brand })),
                    ]}
                />
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Rango de precio</div>
                <div className="grid grid-cols-2 gap-1">
                    <Input
                        type="number"
                        inputMode="numeric"
                        value={filters.minPrice}
                        onChange={(e) => {
                            patch({ minPrice: e.target.value });
                        }}
                        placeholder="Mín"
                        aria-label="Precio mínimo en pesos"
                        className={cn(FIELD, 'tabular-nums')}
                    />
                    <Input
                        type="number"
                        inputMode="numeric"
                        value={filters.maxPrice}
                        onChange={(e) => {
                            patch({ maxPrice: e.target.value });
                        }}
                        placeholder="Máx"
                        aria-label="Precio máximo en pesos"
                        className={cn(FIELD, 'tabular-nums')}
                    />
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Financiera aceptada</div>
                <SelectField
                    value={filters.financier}
                    disabled={financiers.length === 0}
                    label="Filtrar por financiera que acepta el equipo"
                    onChange={(financier) => {
                        patch({ financier });
                    }}
                    options={[
                        { value: '', label: 'Cualquiera' },
                        ...financierChips(financiers).map((chip) => ({
                            value: chip.code,
                            label: `Con ${chip.name}`,
                        })),
                    ]}
                />
            </div>
        </FiltersColumn>
    );
}

/** Chip de nivel del mock: borde y texto del color del nivel; prendido, relleno. */
function LevelChip({
    level,
    active,
    disabled,
    title,
    onClick,
}: {
    level: PlanLevel;
    active: boolean;
    disabled: boolean;
    title?: string;
    onClick: () => void;
}) {
    const tone = LEVEL_CHIP_CLASS[level];
    return (
        <button
            type="button"
            aria-pressed={active}
            disabled={disabled}
            title={title}
            onClick={onClick}
            className={cn(CHIP, active ? tone.on : cn(tone.off, 'bg-card hover:bg-accent'))}
        >
            {levelLabel(level)}
        </button>
    );
}

/** `<select>` nativo con el chevron propio del `.select` del mock. */
function SelectField({
    value,
    disabled,
    label,
    onChange,
    options,
}: {
    value: string;
    disabled: boolean;
    label: string;
    onChange: (value: string) => void;
    options: { value: string; label: string }[];
}) {
    return (
        <div className="relative">
            <select
                value={value}
                disabled={disabled}
                onChange={(e) => {
                    onChange(e.target.value);
                }}
                aria-label={label}
                className={SELECT}
            >
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            <ChevronDown
                className="pointer-events-none absolute right-1.5 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                aria-hidden
            />
        </div>
    );
}
